/**
 * Inline attachment text preview.
 *
 * Renders an expandable card directly under a session-file chip in the
 * chat transcript. The card streams the raw file bytes from
 * `/api/session/files/<id>/raw`, decodes them as UTF-8, and shows
 * live progress (chars decoded vs total bytes) plus a terminal
 * "full text ready" state. Decoded text is cached in `sessionStorage`
 * keyed by `fileId + sha256[:8]` so reloads do not re-extract.
 *
 * Truncated responses (the server advertised more bytes than it
 * actually sent) trigger an automatic Range-based retry, then fall
 * back to a "partial" badge so the operator knows to download the
 * original for the missing tail.
 *
 * Two action buttons sit on the card:
 *   - **Read full document** — opens the existing PDF/image overlay
 *     for binary kinds; for text it expands the inline preview to
 *     its full content with monospace formatting.
 *   - **Summarize** — emits a `summarize` event the host wires to
 *     "queue a 'summarize <filename>' prompt in the composer".
 */
import { LitElement, css, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { icons } from "../icons.ts";
import {
  ATTACHMENT_TEXT_CACHE_PREFIX,
  formatBytes,
  isTextExtractableMime,
  startExtraction,
  type ExtractProgress,
  type LiveExtraction,
} from "./chat-attachment-text-extract.ts";

/** What a session file looks like from the transcript. */
export interface InlinePreviewFile {
  fileId: string;
  filename: string;
  mediaType: string;
  size: number;
  sha256?: string;
}

export interface InlinePreviewOptions {
  /** Resolved raw URL — auth/credentials are baked in by the host. */
  resolveUrl?: (file: InlinePreviewFile) => string;
  /** Optional: read the full PDF/image in the existing overlay. */
  onOpenOverlay?: (file: InlinePreviewFile) => void;
  /** Optional: enqueue a "summarize <filename>" prompt. */
  onSummarize?: (file: InlinePreviewFile, text: string) => void;
  /** Optional: open the chat composer pre-populated with the text. */
  onUseInComposer?: (file: InlinePreviewFile, text: string) => void;
  /**
   * If true, start extraction as soon as the component mounts.
   * Defaults to `false` (the user clicks "Read full document" to
   * begin). The persistence layer means re-clicks are near-instant
   * after the first run.
   */
  autoStart?: boolean;
}

/** Cap on preview chars in the inline pane (full text still served via action). */
const PREVIEW_CHARS_CAP = 6_000;

@customElement("chat-attachment-inline-preview")
export class ChatAttachmentInlinePreview extends LitElement {
  @property({ attribute: false }) file: InlinePreviewFile | null = null;
  @property({ attribute: false }) options: InlinePreviewOptions = {};
  /** User-driven expand/collapse. `true` after clicking the trigger. */
  @state() private expanded = false;
  @state() private progress: ExtractProgress | null = null;
  private live: LiveExtraction | null = null;
  private abortController: AbortController | null = null;

  static styles = css`
    :host {
      display: block;
      margin: 6px 0 0 0;
      max-width: 560px;
    }
    :host([hidden]) { display: none; }

    .card {
      border: 1px solid var(--fleet-border, rgba(0, 170, 255, 0.18));
      border-radius: 8px;
      background: var(--fleet-surface, rgba(8, 14, 22, 0.6));
      overflow: hidden;
      font-size: 12px;
      color: var(--fleet-text, #d4dbe5);
    }
    :root[data-theme="light"] .card {
      background: rgba(248, 250, 253, 0.95);
      border-color: rgba(0, 120, 200, 0.22);
      color: #1a2a3a;
    }

    .header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      cursor: pointer;
      user-select: none;
    }
    .header:hover { background: rgba(0, 170, 255, 0.06); }
    .header__title {
      flex: 1 1 auto;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .header__chev {
      flex: 0 0 auto;
      transition: transform 120ms ease-out;
      color: var(--fleet-muted, #8a96a8);
    }
    :host([data-expanded]) .header__chev { transform: rotate(90deg); }

    .badge {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 1px 6px;
      border-radius: 999px;
      font-size: 10.5px;
      letter-spacing: 0.02em;
      font-weight: 600;
      background: rgba(0, 170, 255, 0.12);
      color: var(--fleet-cyan, #00aaff);
    }
    .badge--ready {
      background: rgba(34, 197, 94, 0.16);
      color: #34d399;
    }
    .badge--partial {
      background: rgba(245, 158, 11, 0.18);
      color: #f59e0b;
    }
    .badge--error {
      background: rgba(239, 68, 68, 0.18);
      color: #f87171;
    }

    .body {
      padding: 0 10px 10px 10px;
      border-top: 1px solid var(--fleet-border, rgba(0, 170, 255, 0.12));
    }
    .body--collapsed { display: none; }

    .progress {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 2px;
      color: var(--fleet-muted, #8a96a8);
      font-size: 11px;
    }
    .progress__bar {
      flex: 1 1 auto;
      height: 4px;
      border-radius: 2px;
      background: rgba(0, 170, 255, 0.12);
      overflow: hidden;
    }
    .progress__fill {
      height: 100%;
      background: var(--fleet-cyan, #00aaff);
      transition: width 120ms ease-out;
    }
    .progress__count {
      flex: 0 0 auto;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }

    .preview {
      margin-top: 6px;
      max-height: 280px;
      overflow: auto;
      padding: 8px 10px;
      border-radius: 6px;
      background: rgba(0, 0, 0, 0.18);
      font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace;
      font-size: 11.5px;
      line-height: 1.5;
      white-space: pre-wrap;
      word-break: break-word;
    }
    :root[data-theme="light"] .preview {
      background: rgba(0, 0, 0, 0.04);
    }

    .actions {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
      margin-top: 8px;
    }
    .actions button {
      all: unset;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 9px;
      border-radius: 6px;
      border: 1px solid var(--fleet-border, rgba(0, 170, 255, 0.22));
      background: transparent;
      color: inherit;
      font-size: 11.5px;
      font-weight: 600;
      cursor: pointer;
      box-sizing: border-box;
    }
    .actions button:hover {
      background: rgba(0, 170, 255, 0.1);
      border-color: rgba(0, 170, 255, 0.4);
    }
    .actions button:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
    .actions button.primary {
      background: var(--fleet-cyan, #00aaff);
      color: #0a1018;
      border-color: transparent;
    }
    .actions button.primary:hover {
      background: #1ebcff;
    }
    .actions svg {
      width: 12px;
      height: 12px;
      stroke: currentColor;
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    .error {
      padding: 6px 0;
      color: #f87171;
      font-size: 11.5px;
    }

    .skipped {
      padding: 6px 0;
      color: var(--fleet-muted, #8a96a8);
      font-size: 11.5px;
    }
  `;

  override connectedCallback(): void {
    super.connectedCallback();
    if (this.options.autoStart) {
      this.startExtraction();
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.abortController?.abort();
    this.live?.abort();
    this.live = null;
  }

  override willUpdate(changed: PropertyValues): void {
    if (changed.has("file") && this.file) {
      // Re-extract only if the file id changed. Otherwise the existing
      // extraction (or cache) is still valid.
      const oldFile = changed.get("file") as InlinePreviewFile | null | undefined;
      if (!oldFile || oldFile.fileId !== this.file.fileId) {
        this.progress = null;
        this.abortController?.abort();
        this.live?.abort();
        this.live = null;
        if (this.expanded && this.options.autoStart !== false) {
          this.startExtraction();
        }
      }
    }
    this.toggleAttribute("data-expanded", this.expanded);
  }

  private toggle(): void {
    this.expanded = !this.expanded;
    if (this.expanded && !this.progress && !this.live) {
      this.startExtraction();
    }
  }

  private startExtraction(): void {
    const f = this.file;
    if (!f) return;
    const url = this.options.resolveUrl?.(f);
    if (!url) {
      this.progress = {
        state: "error",
        chars: 0,
        totalBytes: 0,
        bytesRead: 0,
        truncated: false,
        error: { name: "SessionFileTextExtractionError", code: "unreadable", message: "no URL resolver" } as never,
        attempts: 0,
      };
      this.requestUpdate();
      return;
    }
    if (!isTextExtractableMime(f.mediaType)) {
      this.progress = { state: "skipped", chars: 0, totalBytes: 0, bytesRead: 0, truncated: false, attempts: 0 };
      this.requestUpdate();
      return;
    }
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.live = startExtraction({
      fileId: f.fileId,
      sha256: f.sha256,
      url,
      mime: f.mediaType,
      filename: f.filename,
      signal: this.abortController.signal,
    });
    this.live.subscribe((p) => {
      this.progress = p;
      this.requestUpdate();
    });
    void this.live.done;
  }

  private retry(): void {
    this.progress = null;
    this.live?.abort();
    this.live = null;
    this.startExtraction();
  }

  private openOverlay(): void {
    const f = this.file;
    if (!f) return;
    this.options.onOpenOverlay?.(f);
  }

  private summarize(): void {
    const f = this.file;
    const text = this.progress?.text;
    if (!f || !text) return;
    this.options.onSummarize?.(f, text);
  }

  private useInComposer(): void {
    const f = this.file;
    const text = this.progress?.text;
    if (!f || !text) return;
    this.options.onUseInComposer?.(f, text);
  }

  private renderBadge(p: ExtractProgress | null) {
    if (!p) return nothing;
    if (p.state === "ready" && !p.truncated) {
      return html`<span class="badge badge--ready" data-testid="attachment-text-ready">Full text ready</span>`;
    }
    if (p.state === "partial") {
      return html`<span class="badge badge--partial" data-testid="attachment-text-partial">Partial text</span>`;
    }
    if (p.state === "error") {
      return html`<span class="badge badge--error" data-testid="attachment-text-error">Error</span>`;
    }
    if (p.state === "skipped") {
      return nothing;
    }
    return html`<span class="badge" data-testid="attachment-text-extracting">Extracting…</span>`;
  }

  private renderProgress(p: ExtractProgress) {
    if (p.state === "ready" || p.state === "partial" || p.state === "skipped") {
      // For the "ready" case the badge already says it. For "partial"
      // we additionally surface the count so the operator knows how
      // much they got vs how much exists.
      if (p.state !== "partial") return nothing;
      return this.renderProgressBar(p);
    }
    if (p.state === "error") return nothing;
    return this.renderProgressBar(p);
  }

  private renderProgressBar(p: ExtractProgress) {
    const total = p.totalBytes > 0 ? p.totalBytes : this.file?.size ?? 0;
    const read = p.bytesRead;
    const pct = total > 0 ? Math.min(100, Math.round((read / total) * 100)) : 0;
    return html`
      <div class="progress" role="status" aria-live="polite" data-testid="attachment-text-progress">
        <div class="progress__bar" aria-hidden="true">
          <div class="progress__fill" style="width:${pct}%"></div>
        </div>
        <span class="progress__count" data-testid="attachment-text-count">
          ${p.chars.toLocaleString()} char${p.chars === 1 ? "" : "s"}
          ${total > 0 ? html` / ${formatBytes(total)}` : nothing}
        </span>
      </div>
    `;
  }

  private renderBody() {
    const p = this.progress;
    if (!this.expanded) return nothing;
    if (!p) {
      return html`<div class="progress" role="status" aria-live="polite">Preparing…</div>`;
    }
    if (p.state === "error") {
      const code = p.error?.code ?? "unreadable";
      const message = p.error?.message ?? "extraction failed";
      return html`
        ${this.renderProgress(p)}
        <div class="error" data-testid="attachment-text-error-message">
          <strong>${code}:</strong> ${message}
        </div>
        <div class="actions">
          <button
            type="button"
            @click=${() => this.retry()}
            data-testid="attachment-text-retry"
            aria-label="Retry extraction"
          >
            ${icons.refreshCw}
            <span>Retry</span>
          </button>
        </div>
      `;
    }
    if (p.state === "skipped") {
      return html`
        <div class="skipped" data-testid="attachment-text-skipped">
          Inline text preview not available for this file type.
        </div>
        <div class="actions">
          <button
            type="button"
            class="primary"
            @click=${() => this.openOverlay()}
            data-testid="attachment-text-read-full"
            aria-label="Read full document"
          >
            ${icons.maximize}
            <span>Read full document</span>
          </button>
        </div>
      `;
    }
    const fullText = p.text ?? "";
    const truncated = fullText.length > PREVIEW_CHARS_CAP;
    const preview = truncated ? fullText.slice(0, PREVIEW_CHARS_CAP) : fullText;
    return html`
      ${this.renderProgress(p)}
      <div class="preview" data-testid="attachment-text-preview" aria-label="Extracted text">
${preview || html`<span style="opacity:0.6">(no text decoded yet)</span>`}
      </div>
      <div class="actions">
        <button
          type="button"
          class="primary"
          @click=${() => this.openOverlay()}
          data-testid="attachment-text-read-full"
          aria-label="Read full document"
        >
          ${icons.maximize}
          <span>Read full document</span>
        </button>
        <button
          type="button"
          ?disabled=${!fullText}
          @click=${() => this.summarize()}
          data-testid="attachment-text-summarize"
          aria-label="Summarize"
        >
          ${icons.fileText}
          <span>Summarize</span>
        </button>
        <button
          type="button"
          ?disabled=${!fullText}
          @click=${() => this.useInComposer()}
          data-testid="attachment-text-use-in-composer"
          aria-label="Use in composer"
        >
          ${icons.edit}
          <span>Use in composer</span>
        </button>
        ${truncated
          ? html`<span style="opacity:0.6;font-size:10.5px"
              >showing first ${PREVIEW_CHARS_CAP.toLocaleString()} chars of ${fullText.length.toLocaleString()}</span
            >`
          : nothing}
      </div>
    `;
  }

  override render() {
    const f = this.file;
    if (!f) return nothing;
    if (!isTextExtractableMime(f.mediaType) && !this.options.onOpenOverlay) {
      // Nothing useful to render — the file is binary and no overlay
      // handler is wired. Hide entirely.
      return nothing;
    }
    const badge = this.renderBadge(this.progress);
    return html`
      <div class="card" data-testid="attachment-inline-preview" data-file-id=${f.fileId}>
        <div
          class="header"
          role="button"
          tabindex="0"
          aria-expanded=${this.expanded ? "true" : "false"}
          @click=${() => this.toggle()}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              this.toggle();
            }
          }}
        >
          <span class="header__chev" aria-hidden="true">${icons.arrowRight}</span>
          <span class="header__title">${f.filename}</span>
          ${badge}
        </div>
        <div class=${this.expanded ? "body" : "body body--collapsed"}>${this.renderBody()}</div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "chat-attachment-inline-preview": ChatAttachmentInlinePreview;
  }
}

// ---------------------------------------------------------------------------
// Re-exports for tests and call sites
// ---------------------------------------------------------------------------

/**
 * Quick check: is a session file eligible for inline text preview?
 * Currently true for any text-like MIME.
 */
export function attachmentEligibleForInlinePreview(file: { mediaType: string }): boolean {
  return isTextExtractableMime(file.mediaType);
}

/** Lookup helper used by grouped-render to know if a chip should grow a card. */
export function cacheKeyForAttachmentPreview(fileId: string, sha256?: string): string {
  return `${ATTACHMENT_TEXT_CACHE_PREFIX}${fileId}:${(sha256 ?? "").slice(0, 8) || "nohash"}`;
}
